import { cn } from '@spiaggeit/spit-ui'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { KioskTimeoutDialog } from '@/components/Dialog/KioskTimeoutDialog'
import { GridWithRecap } from '@/components/GridWithRecap'
import { ReservationFooter } from '@/components/Reservation/Footer'
import { ReservationRecapCard } from '@/components/ReservationRecapCard'
import { ReservationRecapDetails } from '@/components/ReservationRecapDetails'

import {
  INSERT_PERIOD_PATH,
  PAYMENT_KIOSK_PATH,
  PAY_PATH,
} from '@/app/router/paths.ts'
import { useAppDispatch, useAppSelector } from '@/hooks/store.ts'
import { useAppPath } from '@/hooks/useAppPath.ts'
import { useCartRecapData } from '@/hooks/useCartRecapData.ts'
import { useEcommerceItems } from '@/hooks/useEcommerceItems'
import { useFetchPricePreview } from '@/hooks/useFetchPricePreview.ts'
import { useLoadServices } from '@/hooks/useLoadServices.ts'
import { InsertDataFormType } from '@/hooks/useUserDataForm.ts'
import { InfoFormSection } from '@/routes/InsertData/InfoFormSection'
import { PromoFormSection } from '@/routes/InsertData/PromoFormSection'
import { useFormatPayload } from '@/routes/InsertData/useFormatPayload.ts'
import { isKioskSelector } from '@/store/appSlice.ts'
import { cartSlice } from '@/store/cartSlice'
import { SESSION_STORAGE_CART_CREATION_TIMESTAMP_KEY } from '@/store/sessionStorageMiddleware'
import { pushInsertDataCheckoutSequence } from '@/utils/analytics/ecommerceDataLayer'
import { ONE_MINUTE, REG_ID_QUERY_PARAM_NAME } from '@/utils/constants.ts'
import { isBackendErrorResponse } from '@/utils/typeGuards.ts'
import { DogFormSection } from './DogFormSection'
import { InvoiceFormSection } from './InvoiceFormSection'
import { UserFormSection } from './UserFormSection'

export const InsertData = () => {
  const { t } = useTranslation()
  const formatPayload = useFormatPayload()
  const { handleSubmit } = useFormContext<InsertDataFormType>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isKiosk = useAppSelector(isKioskSelector)
  const [searchParams] = useSearchParams()

  const payPath = useAppPath(PAY_PATH)
  const kioskPayPath = useAppPath(PAYMENT_KIOSK_PATH)
  const insertPeriodPath = useAppPath(INSERT_PERIOD_PATH)
  const { total } = useCartRecapData()

  const ecommerceItems = useEcommerceItems()
  const ecommerceItemsRef = useRef(ecommerceItems)
  ecommerceItemsRef.current = ecommerceItems
  const insertDataCheckoutPushed = useRef(false)

  useLoadServices()
  useFetchPricePreview()

  useEffect(() => {
    if (insertDataCheckoutPushed.current) return
    insertDataCheckoutPushed.current = true
    pushInsertDataCheckoutSequence(ecommerceItemsRef.current)
  }, [])

  const handlePayment = async (formData: InsertDataFormType) => {
    try {
      const response = await dispatch(
        cartSlice.actions.createCart(formatPayload(formData))
      ).unwrap()
      const timestamp = Date.now()
      sessionStorage.setItem(
        SESSION_STORAGE_CART_CREATION_TIMESTAMP_KEY,
        String(timestamp)
      )
      searchParams.set(REG_ID_QUERY_PARAM_NAME, String(response.cart.order))

      navigate(
        {
          pathname: isKiosk ? kioskPayPath : payPath,
          search: searchParams.toString(),
        },
        { replace: !isKiosk }
      )
    } catch (e) {
      if (isBackendErrorResponse(e)) {
        switch (e.internal_error_code) {
          case 'INTERNAL_MANAGED_ERROR_EXCEPTION_I_R_001':
          case 'INTERNAL_MANAGED_ERROR_EXCEPTION_I_R_010':
          case 'TICKET_OVERBOOKING_EXCEPTION_ERROR_001':
          case 'INTERNAL_MANAGED_ERROR_EXCEPTION_I_SIAE_002':
            navigate(
              `${insertPeriodPath}?createCartError=${e.internal_error_code}`
            )
            break
        }
      }
    }
  }

  return (
    <>
      <GridWithRecap
        as="form"
        className="grow lg:container lg:mb-16"
        onSubmit={handleSubmit(handlePayment)}
      >
        <GridWithRecap.ContentContainer
          className={cn({ 'h-full gap-4': isKiosk })}
        >
          <div
            className={cn('bg-white lg:bg-transparent', {
              'bg-transparent': isKiosk,
            })}
          >
            <div className="flex flex-col gap-2 p-4 lg:hidden">
              {isKiosk ? (
                <p className="font-bold">{t('insertDataForm.recap')}:</p>
              ) : null}

              <ReservationRecapDetails isOpen={isKiosk} />
            </div>
            {isKiosk ? <PromoFormSection /> : <UserFormSection />}
          </div>

          {isKiosk ? null : (
            <>
              <InvoiceFormSection />
              <DogFormSection />
              <PromoFormSection />
            </>
          )}

          <InfoFormSection />

          <ReservationFooter
            className="border-t border-gray-100 bg-white px-4 py-2 lg:hidden"
            requirePolicyAcceptance
            total={total}
          />
        </GridWithRecap.ContentContainer>

        <GridWithRecap.RecapContainer className="hidden lg:flex">
          <ReservationRecapCard requirePolicyAcceptance />
        </GridWithRecap.RecapContainer>
      </GridWithRecap>

      <KioskTimeoutDialog timeout={ONE_MINUTE} />
    </>
  )
}
